import { useCallback, useState } from 'react';

// Column widths for a table whose columns share the row (flex): a column keeps its share of the row until its header has been
// dragged, then it holds the width it was given (px). The widths are remembered in this browser, per storage key and scope
// (an area, a page). Double-clicking the handle puts the column back to its share.
export function useColumnWidths(storageKey, scope, minWidth = 70) {
  const [all, setAll] = useState(() => { try { return JSON.parse(localStorage.getItem(storageKey) || '{}') || {}; } catch { return {}; } });
  const save = (next) => { try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* not remembered */ } };
  const flexOf = (key, fallback = '1 1 0%') => { const w = all[scope]?.[key]; return w ? `0 1 ${w}px` : fallback; };
  const startResize = useCallback((e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = e.currentTarget.parentElement.getBoundingClientRect().width;
    const prevSelect = document.body.style.userSelect;
    const prevCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    function onMove(ev) {
      const w = Math.max(minWidth, Math.round(startW + ev.clientX - startX));
      setAll((prev) => ({ ...prev, [scope]: { ...(prev[scope] || {}), [key]: w } }));
    }
    function onUp() {
      document.body.style.userSelect = prevSelect;
      document.body.style.cursor = prevCursor;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setAll((prev) => { save(prev); return prev; });
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, minWidth, storageKey]);
  const reset = useCallback((key) => {
    setAll((prev) => {
      const area = { ...(prev[scope] || {}) };
      delete area[key];
      const next = { ...prev, [scope]: area };
      save(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, storageKey]);
  return { flexOf, startResize, reset };
}
